import React from 'react';
import { MessageList } from 'components/organisms/chat';
import ChatInput, { type ClassificationOptions } from 'components/chat/ChatInput';
import VerificationInput from 'components/organisms/chat/VerificationInput';
import EvidencePanel from 'components/organisms/chat/EvidencePanel';
import { EvidencePanelProvider } from 'components/organisms/chat/EvidencePanelContext';
import { ProcessingStatus } from 'types/chat.types';
import useChatStore from 'stores/chatStore';
import useSidebarStore from 'stores/sidebarStore';

interface ChatContentProps {
  onSendMessage: (message: string, file?: File, options?: ClassificationOptions, additionalFiles?: File[]) => void;
  processingStatus: ProcessingStatus;
}

const ChatContent: React.FC<ChatContentProps> = ({ onSendMessage, processingStatus }) => {
  const {
    isVerifying,
    verificationStatus,
    submitVerificationAnswer
  } = useChatStore();
  const { isCollapsed } = useSidebarStore();

  const showVerificationInput = isVerifying &&
    verificationStatus === 'awaiting_comprehensive_response';

  const showNormalInput = !isVerifying &&
    processingStatus !== ProcessingStatus.VERIFICATION_IN_PROGRESS;

  return (
    <EvidencePanelProvider>
      <div className={`flex-1 ${isCollapsed ? 'ml-[72px]' : 'ml-64'} flex h-screen overflow-hidden transition-all duration-200`}>
        {/* Left column: messages + input (takes remaining space) */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Scroll region for messages */}
          <div className="flex-1 overflow-y-auto p-5 flex justify-center">
            <MessageList />
          </div>

          {/* Input Area — always aligned with messages */}
          <div className="p-5 flex justify-center">
            {showVerificationInput ? (
              <VerificationInput
                onSubmit={submitVerificationAnswer}
                isSubmitting={false}
                placeholder="Please provide all requested information..."
                helpText="You can provide the information in any format - natural language or structured data."
              />
            ) : showNormalInput ? (
              <ChatInput
                onSendMessage={onSendMessage}
                isProcessing={processingStatus !== ProcessingStatus.IDLE}
              />
            ) : (
              <div className="w-full max-w-3xl text-center py-4">
                <p className="text-sm text-gray-500">
                  {processingStatus === ProcessingStatus.VERIFICATION_IN_PROGRESS
                    ? 'Waiting for verification question...'
                    : processingStatus === ProcessingStatus.FINALIZING
                    ? 'Running HTS classification...'
                    : 'Processing...'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Contextual evidence panel (right side) */}
        <EvidencePanel />
      </div>
    </EvidencePanelProvider>
  );
};

export default ChatContent;
